<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import type { LegalNoticeProps, UiAccordion } from '@afs/design-system'
import { UiButtonVariant, UiAccordionVariant, UiHighlightDirection, UiBlockColor } from '@afs/design-system'
import { computed, onMounted, ref, watch } from 'vue'
import { shouldDisplayInfoBlock, shouldDisplayTextBlock } from '@/utils/services/myselfServices'
import { useMyselfData, type MySelfFormData } from '@/composables/useFormMyself'
import { useFormValidation } from '@/composables/useFormValidation'
import { sendMyselfMail } from '@/composables/useFinancoApi'
import { civilities } from '@/utils/data/myselfData'
import type { mySelfPageData } from '@/pages/(app)/myself/+data'
import type { ParentHomeData } from '@/pages/(app)/index/+data'
import { useGtmOnMyselfFormSubmit, useGtmOnPageMounted } from '@/composables/useGtmTag'
import { openLegalNotice, scrollToLegalNotice } from '@/composables/useAccordion'

const props = withDefaults(defineProps<Props>(), {
  data: () => <ParentHomeData | mySelfPageData>{},
})

type Props = {
  data: ParentHomeData | mySelfPageData
  displayBreadcrumb: boolean
}

const { t } = useI18n()

const { myselfFormData, myselfSchema, loading } = useMyselfData()
const { stepHasBeenSubmitted, getIsStepValid, formatError } = useFormValidation<MySelfFormData>(
  myselfSchema.value,
  myselfFormData,
)

useGtmOnPageMounted('myself')
const track = useGtmOnMyselfFormSubmit()

const altchaPayload = ref('')
const minValue = ref(1000)
const maxValue = ref(75000)

if (props.data.myself?.seoTitle) useFormatSeoContent(props.data.myself.seoTitle, props.data.myself?.hero.subtitle)

const breadcrumb = {
  ariaLabel: t('breadcrumb.ariaLabel'),
  items: [
    { text: t('page.accueil'), to: '/' },
    {
      text:
        props.data.myself?.breadcrumbTitle && props.data.myself.breadcrumbTitle.length > 40
          ? `${props.data.myself.breadcrumbTitle.slice(0, 40)}...`
          : props.data.myself?.breadcrumbTitle,
    },
  ],
}

watch(
  () => myselfFormData.value.wrapper.civility,
  (newValue) => {
    if (newValue === civilities[0].value) {
      myselfFormData.value.wrapper.birthname = ''
    }
  },
)

const legalNoticeAccordion = ref<InstanceType<typeof UiAccordion> | null>(null)
onMounted(() => openLegalNotice(legalNoticeAccordion.value))

const legalNotice = {
  id: 'myself-legal-notice',
  title: t('myself.mentionLegal.title'),
}

const legalBlock = {
  title: props.data.myself?.form.cnil_title,
  content: props.data.myself?.form.cnil_content,
} as LegalNoticeProps

const date = new Date()
date.setFullYear(new Date().getFullYear() - 18)
const birthdateMax = date.toLocaleDateString('fr-ca')

const formStatus = computed(() => ({
  myself: getIsStepValid.value(),
}))
const errors = computed(() => {
  return stepHasBeenSubmitted.value ? formatError.value(formStatus.value['myself'].value.error?.issues) : null
})

const isFormBlocked = computed(() => stepHasBeenSubmitted.value && !formStatus.value['myself'].value.success)

const scrollToForm = () => {
  const form = document.getElementById('formulaire')
  form?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const mailSent = ref(false)
const mailSentKO = ref(false)

const sendMail = async () => {
  loading.value = true
  await sendMyselfMail(myselfFormData.value, altchaPayload.value)
    .then((res) => (mailSentKO.value = res.httpcode && res.httpcode !== 200))
    .catch(() => (mailSentKO.value = true))
    .finally(() => {
      mailSent.value = !mailSentKO.value
      loading.value = false
    })
}

const reachMaxValue = (fieldName: 'amount' | 'savings', value: number | null) => {
  if (value !== null) {
    if (value > maxValue.value) {
      myselfFormData.value.wrapper[fieldName] = maxValue.value
    }
  }
}

const validateForm = () => {
  stepHasBeenSubmitted.value = true

  if (!formStatus.value['myself']?.value.success) {
    scrollToForm()
    return
  }

  track('myself_form', myselfFormData.value.wrapper.amount, myselfFormData.value.wrapper.savings)

  sendMail()
  scrollToForm()
}
</script>

<template>
  <div v-if="data.myself">
    <molecules-ui-breadcrumb
      v-if="displayBreadcrumb"
      v-bind="breadcrumb"
      class="myself__breadcrumb"
    />
    <organisms-ui-hero
      v-if="data.myself.hero"
      v-bind="data.myself.hero"
      :legal-notice-button-label="$t('myself.mentionLegal.buttonLabel')"
      @hero::on-legal-notice-click="scrollToLegalNotice(legalNoticeAccordion)"
      @hero::on-link-button-click="scrollToForm"
    />
    <div class="container my-8">
      <section
        v-if="shouldDisplayTextBlock(data.myself.blockText)"
        class="grid"
      >
        <div class="myself__textblock color-blue-01">
          <h2 class="heading-2">{{ data.myself.blockText.title }}</h2>
          <h3 class="paragraph-2 mb-4 color-red-01">{{ data.myself.blockText.subtitle }}</h3>
          <p class="paragraph-2 mb-2 longtext">
            {{ data.myself.blockText.chapo }}
          </p>
          <molecules-ui-rich-text
            v-if="data.myself.blockText.content"
            :content="data.myself.blockText.content"
          />
        </div>
      </section>
      <section
        v-if="data.myself.argumentsBlock"
        class="myself__arguments grid mt-8"
      >
        <div
          v-if="data.myself.infoBlock"
          class="myself__arguments__block"
        >
          <molecules-ui-focus
            v-if="shouldDisplayInfoBlock(data.myself.infoBlock)"
            v-bind="data.myself.infoBlock"
            @focus::on-event-button-click="scrollToForm()"
          />
        </div>
        <div
          v-if="data.myself.argumentsBlock"
          class="myself__arguments__wrapper"
        >
          <h2 class="myself-arguments__title heading-2 color-blue-01">{{ data.myself.argumentsBlock.title }}</h2>
          <ul class="myself-arguments__list g-3">
            <template
              v-for="(item, index) in data.myself.argumentsBlock.arguments"
              :key="`myself-arguments-${index}`"
            >
              <li v-if="item">
                <molecules-ui-list-item :text="item" />
              </li>
            </template>
          </ul>
          <atoms-ui-button
            class="mt-4"
            :text="$t('myself.mentionLegal.buttonLabel')"
            :variant="UiButtonVariant.TEXT"
            @click="scrollToLegalNotice"
          />
        </div>
      </section>
    </div>
    <section>
      <organisms-ui-block
        v-if="!mailSent && !mailSentKO"
        id="formulaire"
        :title="data.myself.form.title"
        :subtitle="data.myself.form.subtitle"
        :color="UiBlockColor.PURPLE"
        :button-label="$t('myself.formulaire.buttons.send')"
        button-icon="arrow"
        :button-disabled="isFormBlocked || !altchaPayload"
        :loading="loading"
        :legal-notice="legalBlock"
        @block::on-validate-click="validateForm()"
      >
        <div class="myself__form">
          <div class="myself__currency__block">
            <p class="heading-3 color-blue-01 mb-2">{{ $t('myself.formulaire.amount.label') }}</p>
            <atoms-ui-currency
              id="amount"
              v-model="myselfFormData.wrapper.amount"
              class="mb-2"
              symbol="€"
              :min="minValue"
              :max="maxValue"
              :placeholder="$t('myself.formulaire.amount.placeholder')"
              @input="reachMaxValue('amount', myselfFormData.wrapper.amount)"
            />
            <p class="paragraph-3 color-blue-01 pt-2 topbar">
              {{ $t('myself.formulaire.amount.hint') }}
            </p>
            <p
              v-if="errors?.[`wrapper-amount`]"
              class="mt-2 label color-red-01"
            >
              {{ errors['wrapper-amount'] }}
            </p>
          </div>
          <div class="myself__currency__block">
            <p class="heading-3 color-blue-01 mb-2">{{ $t('myself.formulaire.savings.label') }}</p>
            <atoms-ui-currency
              id="savings"
              v-model="myselfFormData.wrapper.savings"
              class="mb-2"
              symbol="€"
              :min="minValue"
              :max="maxValue"
              :placeholder="$t('myself.formulaire.savings.placeholder')"
              @input="reachMaxValue('savings', myselfFormData.wrapper.savings)"
            />

            <p class="paragraph-3 color-blue-01 pt-2 topbar">
              {{ $t('myself.formulaire.savings.hint') }}
            </p>

            <p
              v-if="errors?.[`wrapper-savings`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-savings`] }}
            </p>
          </div>
          <p class="lonely heading-3 color-blue-01">Vous êtes :</p>
          <div class="lonely ml-4">
            <molecules-ui-radio-group
              v-model="myselfFormData.wrapper.civility"
              :options="civilities"
            />

            <p
              v-if="errors?.[`wrapper-civility`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-civility`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="myself-lastname"
              v-model="myselfFormData.wrapper.lastname"
              :max-length="30"
              name="input-name"
              :label="t('myself.formulaire.name.label')"
              :placeholder="t('myself.formulaire.name.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-lastname`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-lastname`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="myself-firstname"
              v-model="myselfFormData.wrapper.firstname"
              :max-length="30"
              name="input-name"
              :label="t('myself.formulaire.firstname.label')"
              :placeholder="t('myself.formulaire.firstname.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-firstname`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-firstname`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="myself-birthname"
              v-model="myselfFormData.wrapper.birthname"
              :max-length="30"
              name="input-name"
              :label="t('myself.formulaire.birthname.label')"
              :placeholder="t('myself.formulaire.birthname.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-birthname`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-birthname`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="myself-birthday"
              v-model="myselfFormData.wrapper.birthday"
              :max-length="30"
              name="input-name"
              type="date"
              :max="birthdateMax"
              :label="t('myself.formulaire.birthdate.label')"
              :placeholder="t('myself.formulaire.birthdate.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-birthday`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-birthday`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="myself-birthCity"
              v-model="myselfFormData.wrapper.birthCity"
              :max-length="32"
              name="myself-birthCity"
              :label="t('myself.formulaire.birthCity.label')"
              :placeholder="t('myself.formulaire.birthCity.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-birthCity`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-birthCity`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="myself-birthCounty"
              v-model="myselfFormData.wrapper.birthCounty"
              :max-length="3"
              name="myself-birthCounty"
              :label="t('myself.formulaire.birthCounty.label')"
              :placeholder="t('myself.formulaire.birthCounty.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-birthCounty`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-birthCounty`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="myself-birthCountry"
              v-model="myselfFormData.wrapper.birthCountry"
              :max-length="32"
              name="myself-birthCountry"
              :label="t('myself.formulaire.birthCountry.label')"
              :placeholder="t('myself.formulaire.birthCountry.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-birthCountry`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-birthCountry`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="myself-phone"
              v-model="myselfFormData.wrapper.phone"
              :max-length="10"
              name="input-name"
              :label="t('myself.formulaire.phone.label')"
              :placeholder="t('myself.formulaire.phone.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-phone`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-phone`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="input-id"
              v-model="myselfFormData.wrapper.mail"
              :max-length="32"
              name="myself-email"
              :label="t('myself.formulaire.email.label')"
              :placeholder="t('myself.formulaire.email.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-mail`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-mail`] }}
            </p>
          </div>
          <div>
            <atoms-ui-input
              id="myself-companyName"
              v-model="myselfFormData.wrapper.companyName"
              :max-length="32"
              name="input-name"
              :label="t('myself.formulaire.company.label')"
              :placeholder="t('myself.formulaire.company.placeholder')"
            />
            <p
              v-if="errors?.[`wrapper-companyName`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-companyName`] }}
            </p>
          </div>
          <div>
            <atoms-ui-checkbox
              id="myself-consent"
              v-model="myselfFormData.wrapper.consent"
              name="is-foreign-birth-place"
              :label="t('myself.formulaire.consent.label')"
            />
            <p
              v-if="errors?.[`wrapper-consent`]"
              class="mt-2 label color-red-01"
            >
              {{ errors[`wrapper-consent`] }}
            </p>
          </div>
          <div
            id="altcha-container"
            class="lonely"
          >
            <Altcha v-model:payload="altchaPayload" />
          </div>
        </div>
      </organisms-ui-block>
      <organisms-ui-block
        v-else-if="mailSent"
        class="pt-7 pb-7"
        :color="UiBlockColor.PURPLE"
      >
        <img
          class="img-check"
          alt="image chevron validé"
          src="@/assets/icons/check.svg"
        />
        <p class="heading-2 color-blue-01">{{ t('myself.formulaire.done.title') }}</p>
        <p class="paragraph-1 color-blue-01 text-center">{{ t('myself.formulaire.done.content') }}</p>
      </organisms-ui-block>
      <organisms-ui-block
        v-else-if="mailSentKO"
        class="pt-7 pb-7"
        :color="UiBlockColor.PURPLE"
      >
        <img
          class="img-warning"
          alt="image panneau attention"
          src="@/assets/illustrations/500-purple.svg"
        />
        <p class="heading-2 color-blue-01">{{ t('myself.formulaire.sendingError.title') }}</p>
        <p class="paragraph-1 color-blue-01 text-center">{{ t('myself.formulaire.sendingError.content') }}</p>
      </organisms-ui-block>
    </section>
    <div>
      <organisms-ui-help
        v-if="data.myself.faqBlock && data.myself.faqList"
        class="mb-8"
        :class="!data.myself.faqBlock.image ? 'mt-8' : ''"
        :title="data.myself.faqBlock.title"
        :contact="data.myself.faqBlock.contact"
        :image="data.myself.faqBlock.image"
      >
        <molecules-ui-accordion
          :items="data.myself.faqList"
          :variant="UiAccordionVariant.SECONDARY"
          name="assurange-page-faq"
        >
          <template
            v-for="question in data.myself.faqList"
            :key="question.id"
            #[question.id]
          >
            <h3
              v-if="question.subtitle"
              class="mb-2 heading-4"
            >
              {{ question.subtitle }}
            </h3>
            <molecules-ui-rich-text :content="question.content" />
          </template>
        </molecules-ui-accordion>
      </organisms-ui-help>
      <section
        v-if="data.myself.promotionalBlock"
        class="container"
      >
        <molecules-ui-highlight
          class="Insurance__highlight"
          v-bind="data.myself.promotionalBlock"
          :direction="UiHighlightDirection.HORIZONTAL"
        />
      </section>
      <section class="mt-8">
        <molecules-ui-accordion
          ref="legalNoticeAccordion"
          class="myself__legalNotice mt-4 mb-8 container"
          :items="[legalNotice]"
        >
          <template
            v-for="item in [legalNotice]"
            :key="item.id"
            #[item.id]
          >
            <molecules-ui-rich-text
              v-if="data.myself.legalNoticeBlock.legalNotice"
              class="mt-3"
              :content="data.myself.legalNoticeBlock.legalNotice"
            />
          </template>
        </molecules-ui-accordion>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'utilities/mq';
@use 'config/spaces';

.myself__breadcrumb {
  position: absolute;
  top: 0;
}

.myself__textblock,
.myself__highlight,
.myself__legalNotice {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 2 / span 10;
  }
}

.myself__legalNotice {
  --scroll-offset: 50px;

  @include mq.media('>=medium') {
    --scroll-offset: 200px;
  }

  &:deep(.Accordion__item) {
    scroll-margin: calc(var(--header-height) + var(--scroll-offset));
  }
}

.myself__arguments {
  align-items: center;

  @include mq.media('<medium') {
    row-gap: map.get(spaces.$sizes, '7');
  }
}

.myself__arguments__wrapper {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: span 5;
  }
}

.myself-arguments__title {
  margin-bottom: map.get(spaces.$sizes, '5');

  @include mq.media('>=medium') {
    margin-bottom: map.get(spaces.$sizes, '6');
  }
}

.myself__currency__block {
  display: flex;
  flex-direction: column;
}

.myself__arguments__block {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 2 / span 5;
  }
}

.myself__form {
  width: 100%;
  height: auto;
  display: grid;
  row-gap: map.get(spaces.$sizes, '7');
  column-gap: map.get(spaces.$sizes, '9');
  transition: all 0.9s;
  -wekit-transition: all 0.9s;
  -moz-transition: all 0.9s;

  @include mq.media('>=medium') {
    grid-template-columns: repeat(2, 1fr);
  }
}

.myself-arguments__list {
  display: flex;
  flex-direction: column;
}

.lonely {
  @include mq.media('>=medium') {
    grid-column-start: 1;
    grid-column-end: 3;
  }
}

.topbar {
  border-top: 1px solid #003b45;
}

.text-center {
  text-align: center;
}

.img-check {
  width: 5em;
}

.img-warning {
  width: 10em;
}
</style>
